import { type FC, type JSX, useMemo } from 'react'
import { useRange, type UseRefinementListProps } from 'react-instantsearch'
import { useIntl } from 'react-intl'

import { productSearchGetRefinementLabel } from '../productSearchGetRefinementLabel'
import {
  ProductSearchRefinement,
  ProductSearchRefinementContext,
} from '../ProductSearchRefinement'
import {
  RATING_DEFAULT_VALUES,
  useProductSearchRefinementStateContext,
} from '../ProductSearchRefinementStateContext'

import { ProductSearchRefinementRatingItem } from './ProductSearchRefinementRatingItem'
import { ProductSearchRefinementRatingListbox } from './ProductSearchRefinementRatingListbox'

const PRODUCT_SEARCH_REFINEMENT_RATING_COUNT = 1

const ratingOptions = Array.from(
  { length: RATING_DEFAULT_VALUES.ratingMax },
  (value, index) => ({
    stars: RATING_DEFAULT_VALUES.ratingMax - index,
  }),
)

type ProductSearchRefinementRatingProps = Pick<
  UseRefinementListProps,
  'attribute'
> & {
  hidden: boolean
}

export const ProductSearchRefinementRating: FC<
  ProductSearchRefinementRatingProps
> = ({ attribute, hidden }) => {
  const intl = useIntl()

  const {
    state: { currentRating, isMobile },
  } = useProductSearchRefinementStateContext()

  const refinementRange = useRange({
    attribute,
  })

  const { canRefine, refine } = refinementRange

  const contextValue = useMemo(
    () => ({
      refinementRange,
      refinementRangeOptions: {
        attribute,
      },
    }),
    [attribute, refinementRange],
  )

  if (hidden || !canRefine) {
    return null
  }

  const formLabel = productSearchGetRefinementLabel(attribute, intl)

  const renderValue = (): JSX.Element => (
    <>
      <span className="truncate">{formLabel}</span>
      {currentRating.ratingMin ? (
        <span className="hidden text-dark-primary-strong desktop:inline">
          {PRODUCT_SEARCH_REFINEMENT_RATING_COUNT}
        </span>
      ) : null}
    </>
  )

  return (
    <ProductSearchRefinementContext value={contextValue}>
      <ProductSearchRefinement
        aria-label={formLabel}
        attribute={attribute}
        data-qa-id={`form-${formLabel}`}
        listboxOpen={isMobile ? true : undefined}
        onChange={(event, value) => {
          if (value !== null) {
            refine([value, RATING_DEFAULT_VALUES.ratingMax])
          }
        }}
        renderValue={renderValue}
        slotProps={{
          popup: {
            /**
             * Rating is always visible on mobile, according to Figma design https://www.figma.com/design/SAgPieIZTeJfx40kjmRwYZ/WSAWA-5583--SERP---HomeOne-migration?node-id=914-13448&m=dev
             */
            keepMounted: isMobile,
          },
          // On mobile the Rating filter is always open inline — not a combobox.
          // Strip combobox attributes so aria-controls does not reference a
          // non-existent listbox, satisfying axe aria-valid-attr-value.
          root: isMobile
            ? {
                'aria-controls': undefined,
                'aria-expanded': undefined,
                role: undefined,
              }
            : undefined,
        }}
        slots={{
          listbox: ProductSearchRefinementRatingListbox,
        }}
        value={currentRating.ratingMin}
      >
        {ratingOptions.map((item) => (
          <ProductSearchRefinementRatingItem
            currentRating={currentRating}
            key={item.stars}
            stars={item.stars}
          />
        ))}
      </ProductSearchRefinement>
    </ProductSearchRefinementContext>
  )
}
